import { useEffect } from 'react';
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Select,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  clearFeaturedAction,
  setFeaturedAction,
  setSimpleField,
  swapFeaturedActions,
} from '../../../store/redux/slices/template.slice';
import {
  clearError,
  clearNamespaceErrors,
} from '../../../store/redux/slices/templateErrors.slice';
import {
  FeaturedAction,
  FeaturedActionSlotKey,
  FeaturedActionType,
} from '../../../store/types/template';
import SectionWrapper from './SectionWrapper';
import OverrideRadio from './OverrideRadio';
import RemoveButton from './RemoveButton';
import DragDotsIcon from '../../../theme/icons/DragDotsIcon';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import StrictModeDroppable from './utils/StrictModeDroppable';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { useEnhancedDesignForTemplate } from './hooks/useEnhancedDesignForTemplate';
import {
  FEATURED_ACTION_LABELS,
  FEATURED_ACTION_SLOT_KEYS,
  FEATURED_ACTION_TYPES,
  createEmptyFeaturedActions,
  featuredActionsEqual,
  isFeaturedActionSlotFilled,
} from './utils/featuredActionTypes';
import {
  APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE as NAMESPACE,
  FEATURED_ACTIONS_SECTION_ID,
} from './utils/appleEnhancedDesignConstants';

const otherSlot = (slot: FeaturedActionSlotKey): FeaturedActionSlotKey =>
  slot === 'one' ? 'two' : 'one';

const FeaturedActionsFields = ({ isEdit }: { isEdit: boolean }) => {
  const dispatch = useAppDispatch();
  const hook = useEnhancedDesignForTemplate();
  const templateType = useAppSelector((s) => s.template.templateType);
  const featuredActions = useAppSelector(
    (s) => s.template.appleEnhancedDesign.featuredActions
  );
  const onLoad = useAppSelector((s) => s.template._appleEnhancedDesignOnLoad);
  const overrideFeaturedActions = useAppSelector(
    (s) => s.template._overrideFeaturedActions
  );
  const errors = useAppSelector((s) => s.templateErrors);
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation(NAMESPACE);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    dispatch(swapFeaturedActions());
    dispatch(clearNamespaceErrors(NAMESPACE));
  };

  const isGeneric = hook.storedDesign === 'generic';
  const slotsChanged = !featuredActionsEqual(
    featuredActions,
    onLoad.featuredActions ?? createEmptyFeaturedActions()
  );
  const turnedOn = isGeneric && onLoad.type !== 'generic';
  const showOverride = isEdit && isGeneric && (slotsChanged || turnedOn);

  const setOverride = (value: boolean) =>
    dispatch(setSimpleField({ field: '_overrideFeaturedActions', value }));

  useEffect(() => {
    if (!showOverride) {
      dispatch(
        setSimpleField({ field: '_overrideFeaturedActions', value: false })
      );
    }
  }, [showOverride, dispatch]);

  if (hook.storedDesign !== 'generic' || !hook.available) {
    return null;
  }

  return (
    <SectionWrapper
      id={FEATURED_ACTIONS_SECTION_ID}
      title="Featured actions"
      tooltip="Tappable buttons shown below the pass in Apple Wallet, each linking out to a URL."
      subtitle="Add up to 2 tappable buttons shown below your pass on iOS 27."
      openOnLoad
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <StrictModeDroppable droppableId="featured-actions">
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {FEATURED_ACTION_SLOT_KEYS.map((slot, index) => {
                const action = featuredActions[slot];
                const sibling = featuredActions[otherSlot(slot)];
                const filled = isFeaturedActionSlotFilled(action);
                const typeError = errors[`${NAMESPACE}.${slot}.type`];
                const urlError = errors[`${NAMESPACE}.${slot}.url`];

                const withUpdatedSlot = (next: Partial<FeaturedAction>) => ({
                  templateType,
                  appleEnhancedDesign: {
                    type: 'generic',
                    featuredActions: {
                      ...featuredActions,
                      [slot]: { ...action, ...next },
                    },
                  },
                });

                return (
                  <Box key={slot}>
                    {index > 0 && <Divider borderColor="lightGrey" my={4} />}
                    <Draggable
                      draggableId={`featured-action-${slot}`}
                      index={index}
                    >
                      {(dragProvided) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                        >
                          <Flex align="flex-start">
                            <Flex
                              {...dragProvided.dragHandleProps}
                              cursor="grab"
                              flexShrink={0}
                              align="center"
                              gap={3}
                              mr={4}
                              ml={-2}
                              h="90px"
                              mt={5}
                            >
                              <DragDotsIcon w="7px" h="13px" color="#989898" />
                              <Box h="100%" w="1px" bg="#989898" mt="5px" />
                            </Flex>
                            <Box flex={1}>
                              <FormControl isInvalid={!!typeError} mb={3}>
                                <Select
                                  value={action.type ?? ''}
                                  placeholder="Select"
                                  onFocus={() =>
                                    handleFocus(`${NAMESPACE}.${slot}.type`)
                                  }
                                  onBlur={handleBlur}
                                  onChange={(e) => {
                                    const value = (e.target.value ||
                                      null) as FeaturedActionType | null;
                                    dispatch(
                                      setFeaturedAction({
                                        slot,
                                        value: { type: value },
                                      })
                                    );
                                    validateField(
                                      `${slot}.type`,
                                      value,
                                      withUpdatedSlot({ type: value })
                                    );
                                  }}
                                >
                                  {FEATURED_ACTION_TYPES.map((type) => (
                                    <option
                                      key={type}
                                      value={type}
                                      disabled={sibling.type === type}
                                    >
                                      {FEATURED_ACTION_LABELS[type]}
                                    </option>
                                  ))}
                                </Select>
                                {typeError && (
                                  <FormErrorMessage
                                    pl={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                  >
                                    <CustomErrorIcon />
                                    {typeError}
                                  </FormErrorMessage>
                                )}
                              </FormControl>
                              <FormControl
                                isInvalid={!!urlError}
                                mb={filled ? 3 : 0}
                              >
                                <Input
                                  value={action.url}
                                  placeholder="https://www.passentry.com"
                                  onFocus={() =>
                                    handleFocus(`${NAMESPACE}.${slot}.url`)
                                  }
                                  onBlur={handleBlur}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    dispatch(
                                      setFeaturedAction({
                                        slot,
                                        value: { url: value },
                                      })
                                    );
                                    validateField(
                                      `${slot}.url`,
                                      value,
                                      withUpdatedSlot({ url: value })
                                    );
                                  }}
                                />
                                {urlError && (
                                  <FormErrorMessage
                                    pl={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                  >
                                    <CustomErrorIcon />
                                    {urlError}
                                  </FormErrorMessage>
                                )}
                              </FormControl>
                              {filled && (
                                <RemoveButton
                                  label="Remove"
                                  onClick={() => {
                                    dispatch(clearFeaturedAction(slot));
                                    dispatch(
                                      clearError(`${NAMESPACE}.${slot}.type`)
                                    );
                                    dispatch(
                                      clearError(`${NAMESPACE}.${slot}.url`)
                                    );
                                  }}
                                />
                              )}
                            </Box>
                          </Flex>
                        </Box>
                      )}
                    </Draggable>
                  </Box>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </StrictModeDroppable>
      </DragDropContext>
      <OverrideRadio
        isVisible={showOverride}
        value={overrideFeaturedActions ? 'all' : 'new'}
        onChange={(value) => setOverride(value === 'all')}
        question="Who should these changes apply to?"
      />
    </SectionWrapper>
  );
};

export default FeaturedActionsFields;
